/* Data for scenario C5CA phase IE 
This data structure holds all the information to create the slides to present during the IE phase.
It mirrors the tree structure that is presented in the Excel file. All nodes in the tree are called actions whether 
they are assessments or interventions. Subactions are the children of the actions in the tree.
Fields:
id: identifies the action-node in the tree. It is a string since it is unique.
label: text for the action as it will appear on the slide
type: indicates whether the action is Required, Not indicated, Optional, Alternative.
    Required: students are asked if they performed it.
    Not indicated: it will be listed on a list of not indicated actions that the student performed accompanied by 
    the feedback on the feedbackErrors field.
    Optional: it will be accepted as correct if performed.
    Alternative: will be used for those interventions that can be performed as alternative to required ones.
    [
    Not used for the pilot study but was used for trial 1:
    Contraindicated: it will be listed under a list of contraindicated actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    Unnecessary: it will be listed under a list of unnecessary actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    Irrelevent: it will be listed under a list of irrelevant actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    ]
AlternativeToIntv: indicates the intervention for which the current one is an alternative. It will only have a value if
the type is Alternative.
feedbackAbsent: feedback given if the action was no performed. Each element in the array corresponds to a paragraph.
feedbackOutOfOrder: feedback given if the action was done late (e.g., if the action is Scene size-up and it was done 
    after Primary Survey). Each element in the array corresponds to a paragraph.
feedbackErrors: feedback if the action was performed incorrectly (e.g., wrong ventilation rate). Each element in the array
 corresponds to a paragraph.
examineIfAbsent: boolean to indicate if this Action needs to be examined when it is Absent (phase E)
examineIfErrors: boolean to indicate if this Action needs to be examined when there are performance errors (phase E)
promptsIfAbsent: prompts to be included in question headers in phase E, to hint on how to reflect when Action was Absent
promptsIfErrors: prompts to be included in question headers in phase E, to hint on how to reflect when Action was performed incorrectly
subActionsList: boolean that indicates whether the subActions need to be listed as bullet points on the slide. If they
    don't need to be listed it is usually because they have been grouped however, the subActions still need to be 
    included in the subActions field because they are used by the analysis to give a summary of their status.
subActions: array with the corresponding subactions (to form the tree)
*/

import {ACTION_TYPES, WAIT_TIFF, WAIT_SANDY, SUM_NODE_NO_FEEDBACK, NO_FEEDBACK, SUM_NODE_FBCK_CHILDREN} from "./constants";

export const C5CA_PhaseIE = {
    Action: {
      id: "top",
      label: "fully assess and manage the patient in this scenario", /* the way it will appear on the slide */
      type: ACTION_TYPES.REQ, /*indicates whether the action was Required, Contraindicated, Unnecessary, or Irrelevant" */
      feedbackAbsent: ["Your log indicates you didn't complete any of these assessment or managment goal. Please restart the scenario and try again"], /*feedback if the action was not performed*/
      feedbackOutOfOrder: [""], /*feedback if the action was done late (e.g., if the action is Scene Size-up 
        and it was done after Primary Survey)*/
      feedbackErrors: [""], /*feedback if the action was performed incorrectly (e.g. wrong Ventilation rate)*/
      examineIfAbsent: false, /* boolean to indicate if this Action needs to be examined (phase E) */
      examineIfErrors: false,
      promptsIfAbsent: "", /* prompts to the included in question in phase E to hint on how to reflect */
      promptsIfErrors: "",
      subActionsList: true, /* boolean that indicates if subActions need to be listed as bullet points on slide */
      subActions: [ /*array with the corresponding subactions (to form the tree)*/
        {
          id: "scene-size-up",
          label: "Put on BSI and do a complete Scene Size-up before entering the scene",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself.", "Your scene size up gives you a big-picture view of what is going on before you even begin examining the patient and includes determining the mechanism of injury and number of patients, requesting additional help if necessary, and considering stabilization of the spine.", "In this case, you would have learned that your patient has been shot multiple times, so you know that controlling bleeding will be one of your top priorities."],
          feedbackOutOfOrder: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself.", "Your scene size up also gives you a big-picture view of what is going on before you even begin examining the patient. This includes determining the mechanism of injury and number of patients, requesting additional help if necessary, and considering stabilization of spine."],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "required-action-BSI",
                label: "Apply BSI",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-scene-safety",
                label: "Determine the scene/situation is safe",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-injury-mechanism",
                label: "Determine the mechanism of injury (MOI) or nature of illness (NOI)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Knowing what caused the patient's injury or illness can allow you to start developing a plan of action before you even assess your patient.", "By determining the mechanism of injury, you would have learned that your patient has been shot multiple times, so you know that controlling bleeding will be one of your top priorities."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-num-patients",
                label: "Determine the number of patients",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Usually you will have one patient, but if there is more than one you will need to plan accordingly."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-requests-additional-help",
                label: "Request additional help if necessary",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Consider the need for extra help based on number of patients, mechanism of injury, and your patient's status."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-considers-stabilize-spine",
                label: "Consider stabilization of spine",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Be sure to consider the need for spinal immobilization in this trauma patient. In this case, you have a low level of concern for a spinal injury; further, know that spinal stabilization is contraindicated in patients with penetrating abdominal wounds."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
          ],
        },
        {
          id: "primary-survey",
          label: "Perform a Primary Survey and manage all identified life threats",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["The primary survey is an assessment of mental status, life-threats, and the patient's ABCs. It is where you will identify and address the patient's most serious injuries.", "In this case, the Primary Survey would have revealed a patient who is lethargic, bleeding profusely from the L upper leg, and breathing rapidly."],
          feedbackOutOfOrder: ["You should have started your Primary Survey after the Scene Size-up and completed it before starting the Secondary Survey. Only interrupt your assessment to control life-threats: (1) Conditions that compromise a patent airway (2) Conditions that compromise breathing or respirations, such as a tension pneumothorax, (3) Conditions that compromise circulation, such as severe bleeding, (4) Cardiac arrest, and any  other potentially life threatening injuries or conditions.  Your patient's condition will deteriorate if these conditions are not addressed before continuing on with your assessment and history taking."],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "general-impression",
                label: "Conduct an Initial Assessment",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You should have performed an Initial Assessment: gathered a general impression, identified any apparent life threats, and determined your patient's level of consciousness.  Your Initial assessment would have revealed a patient who is lethargic and bleeding profusely from the L leg."],
                feedbackOutOfOrder: ["While you may be tempted to jump straight into your ABCs, apparent life threats (i.e., uncontrolled bleeding) should be addressed first, as they can cause a patient to deteriorate quickly. Also, be sure to assess the patient's level of consciousness as an unconscious patient will need a pulse check first to ensure he or she does not require CPR."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assess-patient-condition",
                        label: "State or request a general impression",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Your general impression allows you to start developing a plan of action before you even assess your patient.", "In this case, your general impression would have been of a man with multiple gunshot wounds."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assess-loc",
                        label: "Assess the patient's level of consciousness",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The patient's level of consciousness alters how you progress through your assessment.", "The patient is lethargic with a GCS of 12. This rules out the need for immediate CPR, so you would start your primary survey with an assessment of the patient's airway."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assess-life-threats",
                        label: "Determine the chief complaint/apparent life-threats",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If you had determined apparent life threats, you would have noticed that the patient had life-threatening bleeding from his leg that should be addressed immediately."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "intv-spinal-immobilization-technique-manual-c-spine",
                label: "Apply (or direct a partner to apply) manual c-spine stabilization",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient has been shot in the abdomen and leg. While this may not directly suggest an injury to the spine, if you are unable to definitively rule out the possibility of a spinal injury (e.g., could he have fallen and hit his head after being shot?), you should have taken manual c-spine stabilization. You should have maintained it throughout until you were able to definitively rule out an injury to the spine."],
                feedbackOutOfOrder: ["During trauma situations it is necessary to stabilize the c-spine before continuing as not doing so may cause further injury to the patient."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "Manage severe external bleeding",
                label: "Manage severe external bleeding",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient has severe bleeding from his left upper thigh. There are large vessels in the leg (including the femoral artery) that can bleed rapidly and quickly lead to hemorrhagic shock and death. Remember, that even if you don't see significant bleeding, any significant injury to the upper leg (ex. GSW, femur fracture) has the potential to lead to hemorrhagic shock."],
                feedbackOutOfOrder: ["Severe bleeding is a life threatening injury that should be considered and dealt with prior to the rest of the ABCs. For extremity injuries, this means a tourniquet is likely needed while other injuries may require direct pressure and bandaging or an occlusive dressing (e.g., for a severe neck bleed)."],
                feedbackErrors: [""],
                examineIfAbsent: true,
                examineIfErrors: false,
                promptsIfAbsent: "Didn't realize that the patient's bleeding could be life-threatening; didn't know the best way to manage the patient's bleeding",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "intv-control-bleeding-direct-pressure-and-tourniquet",
                        label: "apply direct pressure and a tourniquet as far proximal to the gun shot wound on the patient's left thigh as possible",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["When you recognize the patient's life-threatening bleed, the first thing you should do is apply direct pressure or direct someone else to do so.", "While the ultimate management of this patient's life-threatening bleed is surgical, it is critical that you stop the bleeding as soon as possible. To do this, you should use a tourniquet and place it as far proximally as possible."],
                        feedbackOutOfOrder: ["You should have managed severe external bleeding by applying a tourniquet before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                        feedbackErrors: ["Be sure to place the tourniquet as far proximally to the wound as possible."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "Didn't realize that the patient's bleeding could be life-threatening; didn't know the best way to manage the patient's bleeding was with a tourniquet",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                            {
                                id: "intv-control-severe-bleeding-technique-direct-pressure",
                                label: "manage severe external bleeding by applying direct pressure",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["When you recognize the patient's life-threatening bleed, the first thing you should do is apply direct pressure or direct someone else to do so."],
                                feedbackOutOfOrder: ["You should have managed severe external bleeding by applying direct pressure to the leg before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                                feedbackErrors: [""],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "intv-control-severe-bleeding-technique-tourniquet",
                                label: "manage severe external bleeding by applying tourniquet as far proximal to the wound as possible",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["While the ultimate management of this patient's life-threatening bleed is surgical, it is critical that you stop the bleeding as soon as possible. To do this, you should use a tourniquet and place it as far proximally as possible."],
                                feedbackOutOfOrder: ["You should have managed severe external bleeding by applying tourniquet before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                                feedbackErrors: ["Be sure to place the tourniquet as far proximally to the wound as possible."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                        ],
                      },
                      {
                        id: "check-bleeding",
                        label: "Confirm that bleeding stopped and pulses are absent",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["After you apply direct pressure and place your tourniquet, you should ensure that the bleeding has stopped and check to ensure that pulses are absent. If this is not the case, you may need a second tourniquet."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "Thought that one tourniquet was enough; did not realize a second tourniquet could be used if necessary",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "request-intervention-status-intv-control-severe-bleeding-technique-direct-pressure",
                            label: "Request status of direct pressure technique",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["After you apply direct pressure, you should ensure that the bleeding has stopped and check to ensure that pulses are absent. If this is not the case, you will need to apply a tourniquet."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "request-intervention-status-intv-control-severe-bleeding-technique-tourniquet",
                            label: "",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["After you place your tourniquet, you should ensure that the bleeding has stopped and check to ensure that pulses are absent. If this is not the case, you may need a second tourniquet."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-2nd-tourniquet", /* not indicated intervention */
                        label: "Second tourniquet",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You achieved hemostasis with the first tourniquet; a second tourniquet is not necessary."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-pack-wound-with-gauze", /* not indicated intervention */
                        label: "Pack wound with gauze or hemostatic gauze",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's gun shot wounds to the abdomen and thigh are shallow and therefore can't be packed with gauze."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-pressure-bandage", /* optional intervention */
                        label: "Pressure dressing",
                        type: ACTION_TYPES.OPT,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: ["You should have applied a pressure dressing before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                        feedbackErrors: ["After dressing the patient's GSWs, a pressure dressing could be placed to maintain bleeding control."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-load-and-go", /* not indicated intervention */
                        label: "Load and go due to uncontrolled bleeding.",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You achieved hemostasis with the first tourniquet."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "airway",
                label: "Assess the patient's airway",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Be sure to assess the airway for patency and intact physical structures.", "You would have found this patient's airway to be patent, as evidenced by the fact that you hear him moaning in pain."],
                feedbackOutOfOrder: ["If your patient is conscious, you should examine the airway before breathing and circulation. If a patient is unconscious, you should assess the pulse first to evaluate for possible cardiac arrest, then go through your ABCs."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                      {
                        id: "airway-is-open",
                        label: "Check if the airway is patent",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The first step of your airway assessment is to see if the patient's airway is open. This patient's moaning indicates he has an open airway. Signs like stridor or gasping may indicate an airway obstruction."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "airway-has-intact-physical-structures",
                        label: "Check if the airway has intact physical structures",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Though this patient did not have any facial trauma, be sure to look for injuries such as gun-shot wounds or facial fractures, as these may affect what basic or advanced airways you can use.  Avoid using a nasopharyngeal airway in patients with a suspected basilar skull fracture or nasal injury."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-open-airway-method-modified-jaw-thrust",
                        label: "Used a modified jaw thrust to check this patient's airway",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Use a modified jaw thrust instead of a head-tilt chin-lift to check this patient’s airway, since there is potential head or spinal injury in assault cases such as this."],
                        feedbackOutOfOrder: ["You should have performed a modified jaw thrust before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                        feedbackErrors: ["A modified jaw thrust could make it easier to perform bag-valve mask ventilation, since it opens the airway."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-airway-patency-technique-suction-airway", /* not indicated intervention */
                        label: "Suction the patient's mouth using a Yankauer (rigid) or Long multi-use catheter",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's airway was patent. There is no need to suction."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-open-airway-method-head-tilt", // not indicated intervention
                        label: "Head tilt chin lift",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A head-tilt chin lift is contraindicated for a patient with suspected c-spine injury as tilting the head could cause further damage to the neck. You should have used a modified jaw thrust to check this patient's airway."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-manual-finger-sweep", /* not indicated intervention */
                        label: "Manual (finger sweep)",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["If there is a visible foreign body in the mouth, it should be removed. However, a blind finger sweep is contraindicated as it could lodge a foreign body deeper into the airway. Further, there is no evidence of foreign body in this patient's mouth."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-magill-forceps-assisted", /* not indicated intervention */
                        label: "Magill forceps assisted removal",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["If there is a visible foreign body in the mouth, it should be removed. However, there is no evidence of a foreign body in this patient's mouth."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-heimlich-maneuver", /* not indicated intervention */
                        label: "Heimlich Maneuver",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You have no evidence the patient is choking on an object so the Heimlich maneuver is not appropriate at this time."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-back-blows-and-chest-thrusts", /* not indicated intervention */
                        label: "Back blows and chest thrusts",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You have no evidence the patient is choking on an object so back blows and chest thrusts are not appropriate at this time."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-oropharyngeal-airway", /* not indicated intervention */
                        label: "Oropharyngeal airway (OPA)",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["While an airway adjunct may be helpful in this case, the patient is conscious and he would likely refuse an oral airway."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-orotracheal-intubation", /* not indicated intervention */
                        label: "Orotracheal intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with a BVM. There is no airway injury or lung injury and the patient's GCS is 12. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-nasotracheal-intubation", /* not indicated intervention */
                        label: "Nasotracheal intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with a BVM. There is no airway injury or lung injury and the patient's GCS is 12. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-insert-advanced-airway", /* not indicated intervention */
                        label: "Insert advanced/rescue airway",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with a BVM. There is no airway injury or lung injury and the patient's GCS is 12. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-rapid-sequence-intubation", /* not indicated intervention */
                        label: "Rapid-sequence intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["RSI is not in the paramedic's scope of practice in Pennsylvania. This patient's respiratory status can be stabilized with a BVM. There is no airway injury or lung injury and the patient's GCS is 12. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-sedation-assisted-intubation", /* not indicated intervention */
                        label: "Sedation-assisted intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with a BVM. There is no airway injury or lung injury and the patient's GCS is 12. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-needle-cricothyrotomy", /* not indicated intervention */
                        label: "Needle cricothyrotomy",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A cricothyrotomy is indicated when there is an upper airway obstruction that cannot be removed. This patient has a patent airway."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought the patient had an upper airway obstruction",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-surgical-cricothyrotomy", /* not indicated intervention */
                        label: "Surgical cricothyrotomy",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A cricothyrotomy is indicated when there is an upper airway obstruction that cannot be removed. This patient has a patent airway."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought the patient had an upper airway obstruction",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "breathing",
                label: "Assess the patient's breathing",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["After assessing the patient's airway, assess their breathing by listening for breath sounds and observing their respiratory rate, rhythm, and effort.", "You would have found shallow and rapid breaths, but with clear and equal bilateral lungs sounds."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "breathing-checks-rate",
                        label: "rate",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A significantly elevated or depressed respiratory rate can suggest impending respiratory failure.", "This patient's tachypnea suggests the need for some respiratory support."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "breathing-checks-rhythm",
                        label: "rhythm",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Although not found in this case, a patient who is breathing irregularly needs ventilatory support."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "breathing-checks-quality",
                        label: "quality",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Sounds like stridor, crackles, or wheezing will help you diagnose and manage your patient's respiratory problems.", "This patient had rapid breathing but had clear lung sounds, suggesting that his lungs were likely uninjured."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        examineIfErrors: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "Manage inadequate breathing",
                label: "Manage inadequate breathing",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Despite not being able to detect an SpO2 when obtaining vitals, your primary survey would have revealed a patient in respiratory distress with rapid, shallow breaths. This patient requires help with both ventilation and oxygenation."],
                feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination.", "Additionally, problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                feedbackErrors: [""],
                examineIfAbsent: true,
                examineIfErrors: false,
                promptsIfAbsent: "Didn't realize that the patient's respiratory status was threatened; didn't know the best way to support this patient's breathing",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "intv-manage-breathing-NPA-and-BVM",
                        label: "insert an NPA and ventilate the patient at a rate of 20 BPM while titrating oxygen at a flow rate of 15 L/min to a target SpO2 >= 90%",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient requires respiratory support and responds by groaning in pain. This is a patient who may benefit from an NPA to help you best oxygenate and ventilate him. He has a gag reflex, so you would want to avoid an OPA.","When you are assessing the patient's airway and breathing, you should have noticed that the patient's respiratory rate is fast and shallow, and his mental status is waning (GCS 12). This patient would benefit from ventilatory support, not just oxygenation. Further, this patient is in hemorrhagic shock. Remember that shock is the inadequate delivery of oxygen, so a key part of the management of shock includes administration of oxygen regardless of the patient's SpO2 or respiratory exam."],
                        feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey.", "Additionally, problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                        feedbackErrors: ["This patient should be ventilated using a BVM hooked up to O2, turned up to 15LPM."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "Didn't realize that the patient's respiratory status was threatened; thought this patient only needed oxygenation; didn't remember that oxygen was part of the management of shock",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                            {
                                id: "intv-nasopharyngeal-airway",
                                label: "Insert a basic airway adjunct",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["This patient requires respiratory support and responds by groaning in pain. This is a patient who may benefit from an NPA to help you best oxygenate and ventilate him. He has a gag reflex, so you would want to avoid an OPA."],
                                feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey."],
                                feedbackErrors: [""],
                                examineIfAbsent:false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "intv-ventilation-technique-bag-valve-mask",
                                label: "ventilating the patient at a rate of 20 BPM, while titrating oxygen at a flow rate of 15 L/min to a target SpO2 >= 90%",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["When you are assessing the patient's airway and breathing, you should have noticed that the patient's respiratory rate is fast and shallow, and his mental status is waning (GCS 12). This patient would benefit from ventilatory support, not just oxygenation. Further, this patient is in hemorrhagic shock. Remember that shock is the inadequate delivery of oxygen, so a key part of the management of shock includes administration of oxygen regardless of the patient's SpO2 or respiratory exam."],
                                feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                                feedbackErrors: ["This patient should be ventilated using a BVM hooked up to O2, turned up to 15LPM."],
                                examineIfAbsent: true,
                                examineIfErrors: false,
                                promptsIfAbsent: "Didn't realize that the patient's respiratory status was threatened; thought this patient only needed oxygenation; didn't remember that oxygen was part of the management of shock",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                        ],
                      },
                      {
                        id: "reassess-AB",
                        label: "Check that airway/breathing interventions are working by checking relevant vitals--in particular, SpO2",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after starting bag-mask ventilation, you want to see if the patient's SpO2 is improved. If not, you will want to reflect on why you did not see the expected improvements? Did you select an incorrect intervention? Do you need to escalate your therapies? Are you performing the intervention correctly?"],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "request-vitals-Spo2",
                            label: "check Spo2",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after starting bag-mask ventilation, you want to see if the patient's SpO2 is improved. If not, you will want to reflect on why you did not see the expected improvements? Did you select an incorrect intervention? Do you need to escalate your therapies? Are you performing the intervention correctly?"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                      {
                        id: "intv-supplemental-oxygen-device-nasal-cannula", /* optional intervention */
                        label: "Nasal cannula",
                        type: ACTION_TYPES.OPT,
                        feedbackAbsent: ["This patient was tachypneic and taking shallow breaths. Paired with your inability to capture his SpO2, this suggests that he needs assistance with both ventilation and oxygenation. While the use of nasal canula would help with oxygenation, it would have been more effective to move directly toward BVM ventilation."],
                        feedbackOutOfOrder: ["Oxygen administration should be performed during the primary survey as this intervention falls under 'Breathing' in the ABC's."],
                        feedbackErrors: ["This patient was tachypneic and taking shallow breaths. Paired with your inability to capture his SpO2, this suggests that he needs assistance with both ventilation and oxygenation. While the use of nasal canula would help with oxygenation, it would have been more effective to move directly toward BVM ventilation."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-supplemental-oxygen-device-non-rebreather-mask", /* optional intervention */
                        label: "Non-rebreather mask",
                        type: ACTION_TYPES.OPT,
                        feedbackAbsent: ["This patient was tachypneic and taking shallow breaths. Paired with your inability to capture his SpO2, this suggests that he needs assistance with both ventilation and oxygenation. While the use of a non-rebreather mask would help with oxygenation, it would have been more effective to move directly toward BVM ventilation."],
                        feedbackOutOfOrder: ["Oxygen administration should be performed during the primary survey as this intervention falls under 'Breathing' in the ABC's."],
                        feedbackErrors: ["This patient was tachypneic and taking shallow breaths. Paired with your inability to capture his SpO2, this suggests that he needs assistance with both ventilation and oxygenation. While the use of a non-rebreather mask would help with oxygenation, it would have been more effective to move directly toward BVM ventilation."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-pleural-decompression", /* not indicated intervention */
                        label: "Needle decompression",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have a penetrating chest wound."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "circulation",
                label: "Assess the patient's circulation",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Evaluate the patient's pulse and skin to assess the patient for shock.  Be sure to also perform a gross blood sweep.", "You would have noticed that your patient had absent radial pulses and a rapid, weak central pulse, concerning for poor peripheral perfusion and shock."],
                feedbackOutOfOrder: ["All unconscious patients should have a pulse check before starting the ABCs. If the patient is pulseless, you will start high-quality CPR immediately."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "pulse-checks",
                        label: "check pulse",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["First check for a radial pulse. However, if you don't feel one, you want to check a carotid pulse.", "This patient's radial pulses were absent, but he did have a weak carotid pulse, which would prompt you to worry about shock.", "Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure.", "While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia.", "A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: true,
                        subActions: [
                          {
                            id: "prompt-to-specify-pulse",
                            label: "check the appropriate pulse(s)",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["First check for a radial pulse. However, if you don't feel one, you want to check a carotid pulse.", "This patient's radial pulses were absent, but he did have a weak carotid pulse, which would prompt you to worry about shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: ["If you cannot appreciate a distal pulse, you should check for a carotid pulse to determine if the patient is truly pulseless or if their distal perfusion is poor."],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-pulse-rate-rhythm-quality",
                            label: "check pulse rate, rhythm, and quality",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure.", "While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia.", "A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [
                              {
                                id: "checks-pulse-rate",
                                label: "rate",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure."],
                                feedbackOutOfOrder: [""],
                                feedbackErrors: [""],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "checks-pulse-rhythm",
                                label: "rhythm",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia."],
                                feedbackOutOfOrder: [""],
                                feedbackErrors: [""],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "checks-pulse-quality",
                                label: "quality",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                                feedbackOutOfOrder: [""],
                                feedbackErrors: [""],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                            ],
                          },
                        ],
                      },
                      {
                        id: "checks-skin",
                        label: "check skin",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Pallor, which you would have noted on this patient, is a sign of inadequate oxygenation and may be caused by shock or respiratory failure. Additionally, this patient's cold skin suggests decompensated shock. Moreover, this patient had diaphoretic skin, which in this case is non-specific, but should prompt you to think about shock."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: true,
                        subActions: [
                          {
                            id: "checks-skin-color",
                            label: "color",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Pallor, which you would have noted on this patient, is a sign of inadequate oxygenation and may be caused by shock or respiratory failure."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-skin-temperature",
                            label: "temperature",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["This patient's cold skin suggests decompensated shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-skin-condition",
                            label: "condition",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["This patient had diaphoretic skin, which in this case is non-specific, but should prompt you to think about shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                      {
                        id: "assess-major-bleeding",
                        label: "Perform a gross blood sweep",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A gross blood sweep will help you identify any life-threatening bleeding you may have missed when forming your general impression, as well as less serious bleeding.", "Beyond the life-threatening bleeding from his left leg, the patient also has minor bleeding from his left upper abdomen."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "transport-decision",
                label: "Make appropriate transport decisions based on your findings so far",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your ABCs and evaluation for life threats and level of consciousness will dictate where you transfer your patient.", "Because of the patient's life-threatening bleeding, he should be quickly transferred to the level II trauma center for definitive management."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "decision-is-load-and-go",
                        label: "Determine that this is a critical patient",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient is in hemorrhagic shock and needs to get to definitive management as soon as possible. You will not be able to fix him in the field."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient is in hemorrhagic shock and needs to get to definitive management as soon as possible. You will not be able to fix him in the field."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "Didn't realize this was a critically ill patient; felt this patient should be further stabilized on site prior to transfer",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-transport",
                        label: "Decide to transport this patient to the Level 2 trauma center by ground ambulance.",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient should be evaluated at the level II trauma center that is 18 minutes away, where they can provide definitive care for his injuries. He should be taken by ambulance with lights and sirens."],
                        feedbackOutOfOrder: ["Initial transport decisions should be made following the 'Primary Survey' (ABCs) because that is when the provider decides whether the patient can be further stabilized on scene or taken directly to a hospital in a 'load and go' scenario. The secondary assessment is only performed on scene if the situation is NOT a 'load and go.' Making transport decisions early is especially important if you are calling for an air ambulance to account for the time it takes for them to arrive."],
                        feedbackErrors: ["This patient should be evaluated at the level II trauma center that is 18 minutes away, where they can provide definitive care for his injuries. He should be taken by ambulance with lights and sirens."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-call-for-air-ambulance", /* not indicated intervention */
                        label: "Call for air ambulance",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["Air ambulance was not available."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "trauma-expose",
                label: "Trauma expose the patient",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Exposure is necessary to properly inspect the patient for injuries. Only the area being inspected should be uncovered and then recovered to prevent heat loss and hypothermia."],
                feedbackOutOfOrder: ["It is important to expose your patient to ensure that your secondary survey can be performed efficiently -- especially in trauma scenarios where there may be more unknown injuries. Exposing the patient allows the provider to more carefully examine the patient during assessment."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                subActionsList: false,
                subActions: [
                  {
                  id: "exposes-body-parts",
                  label: "Exposed (and recovered) anatomical areas as necessary",
                  type: ACTION_TYPES.REQ,
                  feedbackAbsent: ["Exposure is necessary to properly inspect the patient for injuries. Only the area being inspected should be uncovered and then recovered to prevent heat loss and hypothermia."],
                  feedbackOutOfOrder: ["It is important to expose your patient to ensure that your secondary survey can be performed efficiently -- especially in trauma scenarios where there may be more unknown injuries. Exposing the patient allows the provider to more carefully examine the patient during assessment."],
                  feedbackErrors: [""],
                  examineIfAbsent: false,
                  examineIfErrors: false,
                  promptsIfAbsent: "",
                  promptsIfErrors: "",
                  subActionsList: false,
                  subActions: [],
                  },
                ],
              },
              {
                id: "intv-spinal-immobilization-technique-cervical-collar", /* not indicated intervention */
                label: "Apply an appropriately sized cervical collar",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This patient has no evidence of neck injury. Placing a collar and/or CID will make your examination more difficult and should only be done if you suspect an injury to the neck, whether by history or exam."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
          ],
        },
        {
          id: "history-taking",
          label: "Obtain or direct a partner to obtain a patient history",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["Vital signs will help you decide how to manage your patient, as will a history. Even if you cannot obtain a history from the patient directly, you should still try to get as much information as you can (e.g., from a bystander)."],
          feedbackOutOfOrder: [""],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "required-action-obtains-vitals",
                label: "Baseline vital signs",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Baseline vitals are critical; they will help you assess, manage, and continue to monitor your patient.", "In this scenario, you would have found that this patient was tachycardic, tachypneic and hypotensive, prompting you to worry about shock, most likely hemorrhagic given the rapid bleeding from the leg."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["Baseline vitals are critical; they will help you assess, manage, and continue to monitor your patient. At the minimum, you should have obtained BP, P, R, and SpO2."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-attempt-obtain-sample",
                label: "SAMPLE (if possible)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Take an efficient but thorough history from the patient or the witnesses: symptoms, allergies, medications, past medical history, last oral intake, events preceding.", "You would have learned that the patient was shot in his abdomen and leg while arguing with a group of people."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-obtain-OPQRST",
                label: "OPQRST (if possible)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["In this case the patient is unable to tell you about his pain. Instead, you should ask bystanders for any information they have."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
          ],
        },
        {
          id: "secondary-assessment",
          label: "Perform a Secondary Survey (rapid trauma assessment)",
          type: ACTION_TYPES.REQ,
          feedbackAbsent:["The Secondary Survey is a thorough exam of your patient to ensure you have a complete understanding of what is going on and how you need to manage your patient."],
          feedbackOutOfOrder: [""],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "head",
                label: "examine the patient's head",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Inspecting the face for bruising and palpating for facial deformity may indicate facial fractures.", "Abnormal eye movement or pupils, which this patient fortunately did not have, suggest intracranial injury and should prompt you to rapidly transfer your patient to a trauma center. Additionally, bruising around the eyes may be caused by basilar skull fracture, though this aspect of the exam was normal in this patient.", "Although not found in this case, bleeding from the ear may be indicative of a basilar skull fracture and bruising behind the ear suggests a basilar skull fracture (Battle's sign).", "This patient had no drainage from the nose but consider CSF leak from a basilar skull fracture if you see clear drainage.", "If the patient had had secretions or blood in the mouth, you would have needed to apply suction to clear the airway. Additionally, any visualized foreign bodies in the mouth should be removed. A blind finger sweep is never indicated, as you could lodge a foreign body deeper into the airway. There were no obstructions in this patient’s mouth."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-head-injury",
                        label: "Check head for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-inspects-facial-bones",
                        label: "Inspect and palpate the facial bones",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Inspecting the face for bruising and palpating for facial deformity may indicate facial fractures."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-eyes",
                        label: "Inspect the eyes for PERRLA and bruising (\"raccoon eyes\")",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Abnormal eye movement or pupils, which this patient fortunately did not have, suggest intracranial injury and should prompt you to rapidly transfer your patient to a trauma center.", "Additionally, bruising around the eyes may be caused by basilar skull fracture, though this aspect of the exam was normal in this patient."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                           id: "inspects-eyes-PERRLA",
                           feedbackAbsent: ["Abnormal eye movement or pupils, which this patient fortunately did not have, suggest intracranial injury and should prompt you to rapidly transfer your patient to a trauma center."], 
                           subActionsList: false,
                           subActions: []
                          },
                          {
                           id: "inspects-eyes-racoon",
                           feedbackAbsent: ["This aspect of the exam was normal in this patient, but bruising around the eyes may be caused by basilar skull fracture."], 
                           subActionsList: false,
                           subActions: []
                          }
                        ],
                      },
                      {
                        id: "inspects-ears",
                        label: "Inspect the ears for blood, other fluids, and bruising (\"battle signs\")",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Although not found in this case, bleeding from the ear or bruising behind the ear may be indicative of a basilar skull fracture (Battle's sign)."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                           id: "inspects-ears-blood",
                           feedbackAbsent: ["Although not found in this case, bleeding from the ear may be indicative of a basilar skull fracture."],
                           subActionsList: false,
                           subActions: []
                          },
                          {
                           id: "inspects-ears-bruising",
                           feedbackAbsent: ["Although not found in this case, bruising behind the ear suggests a basilar skull fracture (Battle's sign)."], 
                           subActionsList: false,
                           subActions: []
                          }
                        ],
                      },
                      {
                        id: "inspects-nose",
                        label: "Inspect the nose for blood and other fluids",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient had no drainage from the nose but consider CSF leak from a basilar skull fracture if you see clear drainage."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-mouth",
                        label: "Inspect the mouth for blood, other secretions, and obstructions",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If the patient had had secretions or blood in the mouth, you would have needed to apply suction to clear the airway.", "Additionally, any visualized foreign bodies in the mouth should be removed. A blind finger sweep is never indicated, as you could lodge a foreign body deeper into the airway. There were no obstructions in this patient’s mouth."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "neck",
                label: "examine the patient's neck",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient's neck exam was normal, but remember, tracheal deviation suggests unequal intrathoracic pressure (for example, a pneumothorax). The trachea will be deviated away from the side with increased pressure."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-neck-injury",
                        label: "injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-tracheal-deviation",
                        label: "tracheal deviation",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, tracheal deviation suggests unequal intrathoracic pressure (for example, a pneumothorax). The trachea will be deviated away from the side with increased pressure. Additionally, a palpable step-off suggests a fracture."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-JVD",
                        label: "jugular venous distension (JVD)",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, JVD suggests that the heart is not adequately pushing out blood. Consider obstructive processes like a tension pneumothorax or hemothorax."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-stepoffs",
                        label: "step-offs",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, a palpable step-off suggests a fracture."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "chest",
                label: "examine the patient's chest",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["The presence and quality of breath sounds can clue you into underlying problems (e.g., pneumothorax, asthma). This patient's breath sounds were normal and equal bilaterally.", "Inspecting the shoulders for discoloration symmetry or deformity may identify dislocation or fracture of the shoulder (clavicle, humerus and scapula).  This patient's shoulders were normal.", "The clavicles should be inspected for any evidence of fractures. Though rare, clavicle fractures can cause a pneumothorax. This patient’s clavicles were normal.", "If the patient had had anterior thorax and rib crepitation, you should suspect air outside of the lungs, which would raise your suspicion for a pneumothorax. The patient had symmetric movement of his chest. Recall that paradoxical chest wall motion suggests flail chest, where multiple rib fractures lead to an unstable portion of the chest wall."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-check-breath-sounds",
                        label: "Listen for breath sounds",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The presence and quality of breath sounds can clue you into underlying problems (e.g., pneumothorax, asthma). This patient's breath sounds were normal and equal bilaterally."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                       },
                       {
                        id: "assessment-inspect-shoulders",
                        label: "Inspect and palpate shoulders",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Inspecting the shoulders for discoloration symmetry or deformity may identify dislocation or fracture of the shoulder (clavicle, humerus and scapula).  This patient's shoulders were normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-inspect-clavicles",
                        label: "Inspect and palpates clavicles",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The clavicles should be inspected for any evidence of fractures. Though rare, clavicle fractures can cause a pneumothorax. This patient’s clavicles were normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-anterior-thorax",
                        label: "Inspect and palpate anterior thorax for injury, crepitation, and paradoxial motion or flail segments",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If the patient had had anterior thorax and rib crepitation, you should suspect air outside of the lungs, which would raise your suspicion for a pneumothorax.", "The patient had symmetric movement of his chest. Recall that paradoxical chest wall motion suggests flail chest, where multiple rib fractures lead to an unstable portion of the chest wall."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {id: "inspects-chest-injury",
                           feedbackAbsent: [""],
                           subActions: [],},
                          {id: "inspects-palpates-anterior-thorax-crepitation",
                           feedbackAbsent: ["If the patient had had anterior thorax and rib crepitation, you should suspect air outside of the lungs, which would raise your suspicion for a pneumothorax."],
                           subActions: []},
                          {id: "inspects-anterior-thorax-paradoxical-motion",
                           feedbackAbsent: ["The patient had symmetric movement of his chest. Recall that paradoxical chest wall motion suggests flail chest, where multiple rib fractures lead to an unstable portion of the chest wall."],
                           subActions: []},
                        ],
                      },
                ],
              },
              {
                id: "abdomen-pelvis",
                label: "examine the patient's abdomen",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You should have noticed two gunshot wounds with minimal external bleeding in the left upper quadrant. Despite minimal external bleeding, penetrating abdominal wounds can have significant internal bleeding.","Additionally, on palpation, you should have noticed that the patient's abdomen was rigid, suggesting intra-abdominal blood, likely related to the gunshot wounds.","Finally, a distended abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdomen was non-distended."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "inspects-abdomen-injury",
                        label: "inspect and palpate for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["You should have noticed two gunshot wounds with minimal external bleeding in the left upper quadrant. Despite minimal external bleeding, penetrating abdominal wounds can have significant internal bleeding."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-abdomen-distension",
                        label: "inspect and palpate for distension",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A distended abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdomen was non-distended."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-abdomen-rigidity",
                        label: "inspect and palpate for rigidity",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["On palpation, you should have noticed that the patient's abdomen was rigid, suggesting intra-abdominal blood, likely related to the gunshot wounds."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "assess-pelvis",
                label: "examine the patient's pelvis",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Be sure to do a thorough exam of the pelvis as the pelvis can be a source of life-threatening bleeding. The presence of blood or other fluids may indicate urethral, vaginal or rectal injury. Fortunately, your exam of this patient’s pelvis and genitalia would have been normal."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-checks-pelvis",
                        label: "Inspect and compress the pelvis",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Be sure to do a thorough exam of the pelvis as the pelvis can be a source of life-threatening bleeding. Fortunately, the patient's pelvis exam was normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-genitalia-perineum-blood",
                        label: "Inspect the genitalia/perineum for blood, other fluids.",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The presence of blood or other fluids may indicate urethral, vaginal or rectal injury.","Your exam of this patient’s pelvis and genitalia would have been normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "extremities",
                label: "examine the patient's upper and lower extremities",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Because the patient was in shock, you would have been unable to appreciate any distal pulses.", "Diminished or absent mobility may suggest spinal cord or other nervous system injury. The patient was shot in the left leg. You may have noted an abnormal motor exam related to either pain or neurovascular injury.", "Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "inspects-extremities-injury",
                        label: "Inspect and palpate each extremity for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-injury",
                            feedbackAbsent: [""],
                            subActions: []},
                           {id: "inspects-left-arm-injury",
                            feedbackAbsent: [""],
                            subActions: []},
                           {id: "inspects-right-leg-injury",
                            feedbackAbsent: [""],
                            subActions: []},
                           {id: "inspects-left-leg-injury",
                            feedbackAbsent: [""],
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-pulse",
                        label: "Inspect and palpate each extremity for pulse",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Because the patient was in shock, you would have been unable to appreciate any distal pulses."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-pulse",
                            feedbackAbsent: ["Because the patient was in shock, you would have been unable to appreciate any distal pulses."],
                            subActions: []},
                           {id: "inspects-left-arm-pulse",
                            feedbackAbsent: ["Because the patient was in shock, you would have been unable to appreciate any distal pulses."],
                            subActions: []},
                           {id: "inspects-right-leg-pulse",
                            feedbackAbsent: ["Because the patient was in shock, you would have been unable to appreciate any distal pulses."],
                            subActions: []},
                           {id: "inspects-left-leg-pulse",
                            feedbackAbsent: ["Because the patient was in shock, you would have been unable to appreciate any distal pulses."],
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-motor-function",
                        label: "Inspect and palpate each extremity for motor function",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. The patient was shot in the left leg. You may have noted an abnormal motor exam related to either pain or neurovascular injury."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-motor-ability",
                            feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                            subActions: []},
                           {id: "inspects-left-arm-motor-ability",
                            feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                            subActions: []},
                           {id: "inspects-right-leg-motor-ability",
                            feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                            subActions: []},
                           {id: "inspects-left-leg-motor-ability",
                            feedbackAbsent: ["The patient was shot in the left leg. You may have noted an abnormal motor exam related to either pain or neurovascular injury."],
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-sensory-function",
                        label: "Inspect and palpate each extremity for sensory function",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                           {id: "inspects-left-arm-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                           {id: "inspects-right-leg-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                           {id: "inspects-left-leg-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                        ],
                      }, 
                ],
              },
              {
                id: "posterior",
                label: "inspect and palpate the posterior thorax, lumbar, and buttocks for injury and step-offs",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You will need a partner to help you roll the patient safely in order to do a thorough exam of the posterior thorax, lumbar, and buttocks for injury and step-offs. A palpable step-off, which the patient did not have, would suggest a fracture."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [
                    {
                      id: "inspects-posterior-other-injury",
                      feedbackAbsent: [""],
                      subActions: []
                    },
                    {
                      id: "inspects-posterior-step-offs",
                      feedbackAbsent:["A palpable step-off, which the patient did not have, would suggest a fracture."],
                      subActions: []
                    },
                ],
              },
          ],
        },
        {
          id: "ongoing-mgmt-plan",
          label: "Continue to provide adequate prehospital care until arriving at the receiving facility",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["Full spinal immobilization on a longboard is contraindicated for a patient such as this, with a penetrating abdominal injury.  It would be best to transfer him with a scoop stretcher, or carefully strap him onto a longboard and then move him to the ambulance gurney.", "Even if you don't place a collar on this patient, he needs padding in place to protect his head and neck during transfer, and allow for release of manual c-spine stabilization.", "Even if you had no imminent plans to administer fluids, it is beneficial to preemptively place IVs in case your patient were to deteriorate. Keep in mind that if your patient is bleeding and becoming more and more hypotensive, inserting an IV will become increasingly difficult.", "This patient is in shock as evidenced by his lack of peripheral pulses, pale and diaphoretic skin exam, waning mental status, and hypotension. The mostly likely cause of this is the GSW to the leg. Stopping the bleeding and restoring his intravascular volume will help get him to definitive management: surgery.", ],
          feedbackOutOfOrder: [""],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
              id: "intv-place-on-immobilization-device-or-stretcher",
              label: "Place the patient on a scoop stretcher or other transfer/immobilization device and move him to the ambulance stretcher",
              type: ACTION_TYPES.REQ,
              feedbackAbsent: ["Full spinal immobilization on a longboard is contraindicated for a patient such as this, with a penetrating abdominal injury.  It would be best to transfer him with a scoop stretcher, or carefully strap him onto a longboard and then move him to the ambulance gurney."],
              feedbackOutOfOrder: [""],
              feedbackErrors: ["Full spinal immobilization on a longboard is contraindicated for a patient such as this, with a penetrating abdominal injury.  It would be best to transfer him with a scoop stretcher, or carefully strap him onto a longboard and then move him to the ambulance gurney."],
              examineIfAbsent: false,
              examineIfErrors: false,
              promptsIfAbsent: "",
              promptsIfErrors: "",
              subActionsList: false,
              subActions: [
                {
                  id: "intv-place-on-immobilization-device",
                  label: "Place the patient on a scoop stretcher or other transfer/immobilization device and move him to the ambulance stretcher",
                  type: ACTION_TYPES.REQ,
                  feedbackAbsent: ["Full spinal immobilization on a longboard is contraindicated for a patient such as this, with a penetrating abdominal injury.  It would be best to transfer him with a scoop stretcher, or carefully strap him onto a longboard and then move him to the ambulance gurney."],
                  feedbackOutOfOrder: [""],
                  feedbackErrors: ["Full spinal immobilization on a longboard is contraindicated for a patient such as this, with a penetrating abdominal injury.  It would be best to transfer him with a scoop stretcher, or carefully strap him onto a longboard and then move him to the ambulance gurney."],
                  examineIfAbsent: false,
                  examineIfErrors: false,
                  promptsIfAbsent: "",
                  promptsIfErrors: "",
                  subActionsList: false,
                  subActions: [],
                },
                {
                  id: "intv-place-directly-on-stretcher",
                  label: "Place patient directly on stretcher",
                  type: ACTION_TYPES.ALT,
                  alternativeToIntv: "intv-place-on-immobilization-device",
                  feedbackAbsent: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher."],
                  feedbackOutOfOrder: [""],
                  feedbackErrors: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher.", "In addition, this patient should be kept in a supine position. Remember that shock occurs when the body is unable to adequately perfuse the body's vital organs and periphery. When a patient is kept upright while in shock, cerebral perfusion is further hindered by gravity. Lying the patient down takes gravity out of the equation."],
                  examineIfAbsent: false,
                  examineIfErrors: false,
                  promptsIfAbsent: "",
                  promptsIfErrors: "",
                  subActionsList: false,
                  subActions: []
                },
              ],
            },
              {
                id: "intv-spinal-immobilization-technique-attach-cid",
                label: "apply padding (e.g., CID, towels) to keep head inline with spine",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Even if you don't place a collar on this patient, he needs padding in place to protect his head and neck during transfer, and allow for release of manual c-spine stabilization."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-establish-iv",
                label: "Establish at lease one large bore IV en route",
                type: ACTION_TYPES.OPT,
                feedbackAbsent: ["Even if you had no imminent plans to administer fluids, it is beneficial to preemptively place IVs in case your patient were to deteriorate. Keep in mind that if your patient is bleeding and becoming more and more hypotensive, inserting an IV will become increasingly difficult."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["Even if you had no imminent plans to administer fluids, it is beneficial to preemptively place IVs in case your patient were to deteriorate. Keep in mind that if your patient is bleeding and becoming more and more hypotensive, inserting an IV will become increasingly difficult."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-manage-hemorrhagic-shock",
                label: "Manage hemorrhagic shock",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient is in shock as evidenced by his lack of peripheral pulses, pale and diaphoretic skin exam, waning mental status, and hypotension. The mostly likely cause of this is the GSW to the leg. Stopping the bleeding and restoring his intravascular volume will help get him to definitive management: surgery."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: true,
                examineIfErrors: false,
                promptsIfAbsent: "didn't recognize hemorrhagic shock; didn't remember all the steps in the management of hemorrhagic shock",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                  {
                    id: "intv-control-shock-technique-administer-iv-boluses",
                    label: "Bolusing normal saline or Lactated Ringers to a goal BP of 90mm Hg systolic as soon as possible",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["This patient has been bleeding and requires intravascular support to restore and maintain his blood pressure. Administer an isotonic fluid as rapidly as possible (maximizing the size and number of IVs will help you with this) to a goal BP of 90 systolic."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: ["You want to establish at least 2 IVs as soon as possible. As your patient deteriorates and his blood pressure drops further, IV placement becomes more difficult.", "You should use as large of an IV (i.e., 14 or 16 gauge) as possible; this will allow you to administer fluids rapidly. Remember that the pressure of the flow of a fluid is inversely proportional to the radius to the 4th power.", "Always use an isotonic fluid without dextrose for volume resuscitation. This can be either normal saline or Lactated Ringers.  Given this patient's weight, you could titrate 1360 mL of fluids.", "Your goal of fluid administration is to re-establish an adequate, if not normal BP to ensure adequate oxygenation to the patient's end-organs. If you give too much fluid, you risk creating new problems, such as pulmonary edema."],
                    examineIfAbsent: true,
                    examineIfErrors: false,
                    promptsIfAbsent: "Didn't know how much fluid to give; didn't know what kind of fluid to give",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                  {
                    id: "intv-control-shock-technique-keep-patient-warm",
                    label: "keep the patient warm, using some of the following methods: a) applying heat packs to the patient's groin or armpits, b) covering the patient with blankets, c) turning the heat up in the ambulance.",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["It is important to keep this patient warm because hypothermia can cause additional problems or worsening of the patient's current problems, including hypotension, arrhythmias, or even cardiac arrest."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: ["You chose a warming method that isn't appropriate in this scenario. This patient should be kept warm by one or more of the following methods: apply heat packs to the patient's groin or armpits, cover the patient with blankets, or turn the heat up in the ambulance."],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                  /*{
                    id: "intv-position-patient",
                    label: "maintaining the patient in supine position",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["When you keep a patient in a supine position, you make it easier for the heart to pump blood to the brain. A patient in shock is already not delivering adequate oxygen to the brain; fighting gravity will not make this any easier."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: []
                  },*/
                  {
                    id: "reassess-shock",
                    label: "Check that shock management interventions are working by checking relevant vitals--in particular, BP and P",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after bolusing fluids, you should check to see if the patient's blood pressure and HR have improved. If not, you will want to reflect on why you did not see the expected improvements? Did you select an incorrect intervention? Do you need to escalate your therapies?"],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [
                      {
                        id: "request-vitals-BP",
                        label: "vital BP",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after bolusing fluids, you should check to see if the patient's blood pressure has improved. If not, you will want to reflect on why you did not see the expected improvements? Did you select an incorrect intervention? Do you need to escalate your therapies?"],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "request-vitals-P",
                        label: "check P",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after bolusing fluids, you should check to see if the patient's HR has improved. If not, you will want to reflect on why you did not see the expected improvements? Did you select an incorrect intervention? Do you need to escalate your therapies?"],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                    ]
                  },
                ],
              },
              {
                id: "intv-apply-dressing",
                label: "Manage non-life-threatening injuries--in particular apply sterile dressings to the gun shot wounds on the patient's abdomen",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient has GSWs to the LUQ of his abdomen, which should be dressed once the patient is stabilized."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [
                  {
                    id: "intv-apply-sterile-dressings",
                    label: "Manage non-life-threatening injuries--in particular apply sterile dressings to the gun shot wounds on the patient's abdomen",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["This patient has GSWs to the LUQ of his abdomen, which should be dressed once the patient is stabilized."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: []
                  },
                  {
                    id: "intv-occlusive-dressing",
                    label: "Occlusive dressing",
                    type: ACTION_TYPES.ALT,
                    alternativeToIntv: "intv-apply-sterile-dressings",
                    feedbackAbsent: ["This patient has GSWs to the LUQ of his abdomen, which should be dressed once the patient is stabilized."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                ]
              },
              {
                id: "Reassess the patient",
                label: "Reassess the patient",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change. This includes repeating the primary survey, secondary survey, and vital signs. A critically ill patient should be reassessed more frequently to ensure he or she is still stable-approximately every 5 minutes."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                  {
                        id: "reassess-vitals-plus-frequency",
                        label: "Recheck vital signs every 5 minutes",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly. In particular, in this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient’s breathing, severe bleeding, and shock were adequately managed."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                            {
                              id: "reassess-vital-signs",
                              label: "Recheck vital signs every 5 minutes",
                              type: ACTION_TYPES.REQ,
                              feedbackAbsent: ["Critical patients' vital signs should be reassessed every 5 minutes, since their condition could deteriorate rapidly. In particular, in this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient’s breathing, severe bleeding, and shock were adequately managed."],
                              feedbackOutOfOrder: [""],
                              feedbackErrors: ["In this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient’s breathing, severe bleeding, and shock were adequately managed."],
                              examineIfAbsent: false,
                              examineIfErrors: false,
                              promptsIfAbsent: "",
                              promptsIfErrors: "",
                              subActionsList: false,
                              subActions: [], 
                            },
                            {
                              id: "decision-vitals-frequency",
                              label: "decision vitals frequency",
                              type: ACTION_TYPES.REQ,
                              feedbackAbsent: ["Critical patients' vital signs should be reassessed every 5 minutes, since their condition could deteriorate rapidly."],
                              feedbackOutOfOrder: [""],
                              feedbackErrors: ["Critical patients' vital signs should be reassessed every 5 minutes, since their condition could deteriorate rapidly."],
                              examineIfAbsent: false,
                              examineIfErrors: false,
                              promptsIfAbsent: "",
                              promptsIfErrors: "",
                              subActionsList: false,
                              subActions: [], 
                            },
                        ],
                      },
                      {
                        id: "repeat-primary-survey",
                        label: "Repeat the Primary Survey",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change, which includes the primary survey. For example, a patient with a neck injury whose breathing was initially non-labored may develop stridor as the airway becomes swollen."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "repeat-secondary-survey",
                        label: "Repeat the Secondary Survey",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change, which includes your secondary survey. For example, a patient may lose their pulses if you initially failed to notice and treat a source of hemorrhage."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "intv-contact-receiving-facility",
                label: "Provide a patient report and ETA to the receiving facility",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["The hospital requires notification for all incoming patients 5 -15 prior to arrival, which is especially important for critical patients as the receiving team needs time to prepare equipment and space."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-splint-fracture", /* not indicated intervention */
                label: "Splint fractures",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have any fractures."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-sling", /* not indicated intervention */
                label: "Apply sling",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have any injuries requiring a sling."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-bulky-dressing ", /* not indicated intervention */
                label: "Bulky dressing",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This patient did not have any injuries that needed to be stabilized by a bulky dressing."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-prepare-amputated-part", /* not indicated intervention */
                label: "Prepare amputated part for transport",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have an amputation."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-prepare-and-administer-pain-nausea-vomiting-medications", /* not indicated intervention */
                label: "Prepare and administer medications",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["You are unable to assess pain or nausea in this patient so he should not be given any medicines. Instead, you should focus on stabilizing the patient."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
            },
            {
                id: "intv-contact-medical-command", /* optional intervention */
                label: "Contact Medical Command",
                type: ACTION_TYPES.OPT,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
            },
            {
              id: "intv-walk-patient-to-ambulance", /* not indicated intervention */
              label: "Walk patient to ambulance",
              type: ACTION_TYPES.NOTIND,
              feedbackAbsent: [""],
              feedbackOutOfOrder: [""],
              feedbackErrors: ["This patient cannot be walked to the ambulance.  He suffered severe bleeding from a gunshot wound to his left thigh and is hemodynamically unstable."],
              examineIfAbsent: false,
              examineIfErrors: false,
              promptsIfAbsent: "",
              promptsIfErrors: "",
              subActionsList: false,
              subActions: []
            },
          ],
        },     
    ],
},
}
